import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { FormField } from '@ooh/ui/components/form-field';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { toast } from '@ooh/ui/components/sonner';
import { adminUsers, parseApiError } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';
import { createPasswordFieldSchema } from './password-field-schema';

const SET_PASSWORD_FORM_ID = 'set-password-form';
const DIALOG_MAX_WIDTH = 'max-w-sm';
const DIALOG_Z_INDEX = 'z-[60]';

// AdminSetPasswordSchema (packages/contracts/src/schemas/user-admin.ts)
// stays the server-side/API source of truth and is still enforced there --
// this client-only schema exists so the min-length and digit/symbol
// validation errors render in the active UI language. The password field
// rule itself is shared with create-user-dialog.tsx via
// createPasswordFieldSchema; only the confirm-password match refine is
// dialog-specific.
function createFormSchema(messages: {
  minLength: string;
  requiresNumberOrSymbol: string;
  confirmInvalid: string;
}) {
  return z
    .object({
      password: createPasswordFieldSchema(messages),
      confirmPassword: z.string(),
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: messages.confirmInvalid,
      path: ['confirmPassword'],
    });
}

type SetPasswordForm = z.infer<ReturnType<typeof createFormSchema>>;

interface SetPasswordDialogProps {
  userId: number;
  onClose: () => void;
}

// Admin stopgap password reset (Solution B): a dedicated sub-dialog rather
// than a field on EditUserDialog's form -- keeps the plaintext password out
// of the shared edit-user form state and its PATCH /admin/users/:id payload.
//
// Rendered via ModalDialog (@ooh/ui/components/modal-dialog), which portals
// its content to document.body. This is load-bearing, not cosmetic: this
// dialog is opened from inside EditUserDialog's own <form>, and a plain nested
// <form> here would let the browser (or React's own portal-aware synthetic
// event bubbling -- see React docs on createPortal) route this dialog's
// submit to the OUTER form instead, causing a native GET navigation with the
// plaintext password serialized into the URL. The footer's submit button uses
// the `form` attribute (not physical DOM nesting) to associate with the
// fields' <form>, and the fields' <form> stops propagation on submit as
// defense-in-depth against that same portal-bubbling behavior.
export function SetPasswordDialog({ userId, onClose }: SetPasswordDialogProps) {
  const { t } = useTranslation('admin');
  const api = useApiClient();

  const SetPasswordFormSchema = createFormSchema({
    minLength: t('users.setPassword.fields.minLength'),
    requiresNumberOrSymbol: t('users.setPassword.fields.requiresNumberOrSymbol'),
    confirmInvalid: t('users.setPassword.fields.confirmInvalid'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SetPasswordForm>({
    resolver: zodResolver(SetPasswordFormSchema),
    mode: 'onChange',
  });

  const mutation = useMutation({
    mutationFn: (data: SetPasswordForm) =>
      api.call(adminUsers.setPassword, { id: userId, password: data.password }),
    onSuccess: () => {
      toast.success(t('users.setPassword.success'));
      onClose();
    },
    onError: (err) => {
      toast.error(apiErrorToUserMessage(parseApiError(err), 'mutation'));
    },
  });

  const submitForm = handleSubmit((data) => mutation.mutate(data));

  return (
    <ModalDialog
      onClose={onClose}
      closeLabel={t('users.edit.close')}
      maxWidth={DIALOG_MAX_WIDTH}
      zIndex={DIALOG_Z_INDEX}
      header={<h2 className="text-[15px] font-semibold">{t('users.setPassword.title')}</h2>}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('users.actions.cancel')}
          </Button>
          <Button
            type="submit"
            form={SET_PASSWORD_FORM_ID}
            disabled={!isValid || mutation.isPending}
          >
            {mutation.isPending ? t('users.setPassword.saving') : t('users.setPassword.action')}
          </Button>
        </>
      }
    >
      <form
        id={SET_PASSWORD_FORM_ID}
        onSubmit={(e) => {
          // Defense-in-depth: React replays bubbling synthetic events through
          // the React tree (not the DOM tree) across portal boundaries, so an
          // ancestor <form>'s onSubmit could still fire without this. See the
          // load-bearing comment above.
          e.stopPropagation();
          void submitForm(e);
        }}
        className="space-y-4"
      >
        <FormField
          testId="set-password-password-field"
          label={t('users.setPassword.fields.password')}
          htmlFor="set-password-password"
          error={errors.password?.message}
        >
          <Input id="set-password-password" type="password" {...register('password')} />
        </FormField>
        <FormField
          testId="set-password-confirm-field"
          label={t('users.setPassword.fields.confirmPassword')}
          htmlFor="set-password-confirm"
          error={errors.confirmPassword?.message}
        >
          <Input id="set-password-confirm" type="password" {...register('confirmPassword')} />
        </FormField>
        <p className="text-[12px] text-muted-foreground">{t('users.setPassword.sessionNote')}</p>
      </form>
    </ModalDialog>
  );
}

interface SetPasswordActionProps {
  userId: number;
}

// Encapsulates the trigger button + its own open/close state so EditUserDialog
// only wires one line instead of an extra useState + conditional render --
// keeps EditUserDialog under the max-lines-per-function / complexity lint budget.
export function SetPasswordAction({ userId }: SetPasswordActionProps) {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        {t('users.setPassword.action')}
      </Button>
      {open ? <SetPasswordDialog userId={userId} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
